/**
 * Notification Components
 * 
 * Phase 1: Internal & In-App Only
 * - Bell icon with unread badge
 * - Simple dropdown preview (no complex filtering)
 * - Mark as read functionality
 * 
 * NON-NEGOTIABLE:
 * - Informational only (no auto-actions)
 * - For internal users (Founders/Staff) only
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CheckCheck, AlertTriangle, Package, Clock, Info, X, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ============ NOTIFICATION BELL WITH DROPDOWN ============

export function NotificationBell() {
  const { user, orgId } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasCritical, setHasCritical] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count (for badge)
  const fetchUnreadCount = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/in-app-notifications/unread-count?org_id=${orgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnreadCount(response.data.count);
      setHasCritical(response.data.has_critical);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [orgId]);

  // Fetch notifications (when dropdown opens)
  const fetchNotifications = useCallback(async () => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/api/in-app-notifications/?org_id=${orgId}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    if (!orgId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/api/in-app-notifications/${notificationId}/read?org_id=${orgId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!orgId) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API}/api/in-app-notifications/mark-all-read?org_id=${orgId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      setHasCritical(false);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  // Poll for unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="notification-bell-container" ref={dropdownRef} data-testid="notification-bell">
      <button 
        className={`notification-bell-btn ${hasCritical ? 'has-critical' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span 
            className={`notification-badge ${hasCritical ? 'critical' : ''}`}
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div 
          className="notification-dropdown" 
          data-testid="notification-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: '380px',
            maxHeight: '480px',
            backgroundColor: '#1e222d',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div 
            className="notification-dropdown-header"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#f1f5f9', margin: 0 }}>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={markAllAsRead}
                data-testid="mark-all-read-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          <div 
            className="notification-dropdown-content"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px 0'
            }}
          >
            {loading ? (
              <div className="notification-loading">
                <div className="loading-spinner" />
                <span>Loading...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <Bell size={32} className="empty-icon" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={() => markAsRead(notification.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ NOTIFICATION ITEM ============

function NotificationItem({ notification, onMarkRead }) {
  const { 
    id, 
    notification_type, 
    severity, 
    title, 
    message, 
    is_read, 
    created_at,
    entity_type,
    entity_id,
    metadata
  } = notification;

  const getIconColor = () => {
    switch (notification_type) {
      case 'delivery_date_changed': return '#3b82f6';
      case 'task_slipped': return '#f59e0b';
      case 'po_action_required': return '#f59e0b';
      case 'po_status_changed': return '#3b82f6';
      case 'task_blocked': return '#ef4444';
      case 'task_at_risk': return '#f59e0b';
      case 'invite_sent': return '#22c55e';
      case 'invite_accepted': return '#22c55e';
      case 'invite_expired': return '#64748b';
      default: return '#64748b';
    }
  };

  const getIcon = () => {
    const iconColor = getIconColor();
    switch (notification_type) {
      case 'delivery_date_changed':
        return <Package size={16} style={{ color: iconColor }} />;
      case 'task_slipped':
        return <Clock size={16} style={{ color: iconColor }} />;
      case 'po_action_required':
        return <AlertTriangle size={16} style={{ color: iconColor }} />;
      case 'po_status_changed':
        return <Package size={16} style={{ color: iconColor }} />;
      case 'task_blocked':
        return <X size={16} style={{ color: iconColor }} />;
      case 'task_at_risk':
        return <AlertTriangle size={16} style={{ color: iconColor }} />;
      case 'invite_sent':
      case 'invite_accepted':
      case 'invite_expired':
        return <Bell size={16} style={{ color: iconColor }} />;
      default:
        return <Info size={16} style={{ color: iconColor }} />;
    }
  };

  const getSeverityBorderColor = () => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNavigationUrl = () => {
    if (!entity_type || !entity_id) return null;
    
    const jobId = metadata?.job_id || entity_id;
    
    switch (entity_type) {
      case 'job':
        return `/jobs/${entity_id}`;
      case 'purchase_order':
        return jobId ? `/jobs/${jobId}` : null;
      case 'task':
        return jobId ? `/jobs/${jobId}` : null;
      case 'invite':
        return '/account/team-members';
      default:
        return null;
    }
  };

  const navUrl = getNavigationUrl();

  return (
    <div 
      data-testid={`notification-item-${id}`}
      style={{
        display: 'flex',
        gap: '8px',
        padding: '16px 24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        position: 'relative',
        background: is_read ? 'transparent' : 'rgba(59, 130, 246, 0.05)',
        borderLeft: `3px solid ${getSeverityBorderColor()}`
      }}
    >
      <div style={{
        flexShrink: 0,
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        background: 'rgba(255, 255, 255, 0.03)'
      }}>
        {getIcon()}
      </div>
      
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '4px'
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#f1f5f9',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>{title}</span>
          <span style={{
            fontSize: '12px',
            color: '#64748b',
            whiteSpace: 'nowrap'
          }}>{formatTime(created_at)}</span>
        </div>
        <p style={{
          fontSize: '13px',
          color: '#94a3b8',
          lineHeight: 1.4,
          marginBottom: '8px',
          margin: '0 0 8px 0'
        }}>{message}</p>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {!is_read && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead();
              }}
              data-testid={`mark-read-${id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 'none',
                color: '#64748b',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px'
              }}
            >
              <Check size={12} />
              Mark read
            </button>
          )}
          {navUrl && (
            <a 
              href={navUrl}
              onClick={(e) => {
                if (!is_read) onMarkRead();
              }}
              data-testid={`view-${id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                fontSize: '12px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                textDecoration: 'none'
              }}
            >
              <ExternalLink size={12} />
              View
            </a>
          )}
        </div>
      </div>

      {!is_read && (
        <div style={{
          position: 'absolute',
          top: '50%',
          right: '16px',
          transform: 'translateY(-50%)',
          width: '8px',
          height: '8px',
          background: '#3b82f6',
          borderRadius: '50%'
        }} />
      )}
    </div>
  );
}

// ============ NOTIFICATION BADGE (Standalone) ============

export function NotificationBadge({ count, hasCritical = false }) {
  if (count === 0) return null;
  
  return (
    <span className={`notification-badge-standalone ${hasCritical ? 'critical' : ''}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

export default NotificationBell;
